import { first, values } from 'es-toolkit/compat';
import { DelimitedArrayParam, useQueryParam } from 'use-query-params';

export type ViewerLayoutUrlState = {
  selectedSlideIds?: Array<string | null> | null;
  viewersStainPreferences?: Array<string | null> | null;
};

export const hasExplicitMultiViewerSettings = (settings?: Record<string, Record<number, unknown>>) => {
  const viewerIndices = new Set<string>();

  Object.values(settings || {}).forEach((viewerSettings) => {
    Object.keys(viewerSettings || {}).forEach((viewerIndex) => {
      viewerIndices.add(viewerIndex);
    });
  });

  return viewerIndices.size > 1;
};

export const hasMultiViewerLayoutInUrl = ({ selectedSlideIds, viewersStainPreferences }: ViewerLayoutUrlState) =>
  (selectedSlideIds?.length ?? 0) > 1 || (viewersStainPreferences?.length ?? 0) > 1;

export const shouldAllowViewerUrlStateFallback = ({
  hasExplicitMultiViewerState,
  selectedSlideIds,
  viewersStainPreferences,
}: ViewerLayoutUrlState & {
  hasExplicitMultiViewerState: boolean;
}) => !hasExplicitMultiViewerState && !hasMultiViewerLayoutInUrl({ selectedSlideIds, viewersStainPreferences });

export const getViewerIndexedUrlSetting = <T>({
  settingsByViewer,
  viewerIndex,
  shouldAllowFallback,
}: {
  settingsByViewer: Record<number, T> | undefined;
  viewerIndex: number;
  shouldAllowFallback: boolean;
}) =>
  settingsByViewer?.[viewerIndex] ??
  (shouldAllowFallback && settingsByViewer ? first(values(settingsByViewer)) : undefined);

export const useViewerLayoutUrlState = (): ViewerLayoutUrlState => {
  const [selectedSlideIds] = useQueryParam('selectedSlideIds', DelimitedArrayParam);
  const [viewersStainPreferences] = useQueryParam('viewersStainPreferences', DelimitedArrayParam);

  return {
    selectedSlideIds,
    viewersStainPreferences,
  };
};
